/**
 * Vehicle picker.
 *
 * The chip on the route sheet used to CYCLE: tap it and you got the next
 * vehicle in the list. That leaves the one question the driver actually has
 * unanswered - "is the name on this button the vehicle I am routing as, or the
 * one I would get if I pressed it?" - and getting it wrong means routing a
 * 3.6 m truck as a car, under a bridge it does not fit beneath.
 *
 * So it is a list now: every vehicle shown at once, the active one ticked, and
 * one tap to choose. The dimensions are on each row because they are what
 * changes the route, and because "Hino Truck" and "Car" stop being
 * distinguishable the moment somebody adds a second truck.
 */

import type { VehicleProfile } from '../core/settings';
import { t } from '../core/i18n';

const CAR_ICON = `<svg viewBox="0 0 24 24"><path d="M5 11 L6.5 6.5 C6.8 5.6 7.6 5 8.5 5 h7 c.9 0 1.7.6 2 1.5
  L19 11 M4 11 h16 v5 a1 1 0 0 1 -1 1 h-1 a1 1 0 0 1 -1 -1 v-1 H7 v1 a1 1 0 0 1 -1 1 H5 a1 1 0 0 1 -1 -1 Z"
  fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const TRUCK_ICON = `<svg viewBox="0 0 24 24"><path d="M2 6 h11 v9 H2 Z M13 9 h4 l3 3 v3 h-7 Z"
  fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
  <circle cx="6" cy="17.5" r="1.8" fill="currentColor"/>
  <circle cx="17" cy="17.5" r="1.8" fill="currentColor"/></svg>`;

const TICK = `<svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17.5 L19 7"
  fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

export function vehicleIcon(kind: string): string {
  return kind === 'truck' ? TRUCK_ICON : CAR_ICON;
}

export class VehiclePicker {
  private el: HTMLElement;

  constructor(
    parent: HTMLElement,
    private onPick: (id: string) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'veh-picker';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  close(): void {
    this.el.hidden = true;
  }

  open(vehicles: VehicleProfile[], activeId: string): void {
    this.el.hidden = false;
    this.el.innerHTML = `
      <div class="veh-picker__card">
        <div class="veh-picker__head">
          <span class="veh-picker__title">${t('vehicle.choose')}</span>
          <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">
            <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor"
              stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="veh-picker__list" role="radiogroup">
          ${vehicles.map((v) => this.row(v, v.id === activeId)).join('')}
        </div>
      </div>`;

    this.el
      .querySelector('[data-act=close]')!
      .addEventListener('click', () => this.close());
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el) this.close();
    });
    this.el.querySelectorAll<HTMLElement>('[data-veh]').forEach((row) => {
      row.addEventListener('click', () => {
        this.close();
        this.onPick(row.dataset.veh!);
      });
    });
  }

  private row(v: VehicleProfile, active: boolean): string {
    // Only the dimensions that change routing, and only when they are set.
    const specs = [
      v.height ? `${v.height} m` : '',
      v.weight ? `${v.weight} t` : '',
      v.easyPass ? t('set.easyPass') : '',
    ]
      .filter(Boolean)
      .join(' · ');

    return `
      <button class="veh-row ${active ? 'is-active' : ''}" data-veh="${escapeHtml(v.id)}"
              role="radio" aria-checked="${active}">
        <span class="veh-row__icon">${vehicleIcon(v.kind)}</span>
        <span class="veh-row__body">
          <span class="veh-row__name">${escapeHtml(v.name)}</span>
          ${specs ? `<span class="veh-row__specs">${escapeHtml(specs)}</span>` : ''}
        </span>
        <span class="veh-row__tick" aria-hidden="true">${active ? TICK : ''}</span>
      </button>`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
